import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { MdClose } from "react-icons/md"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

import {
  cdsBroadcastChannel,
  hasFullAccess,
  haveAuth,
  isInKioskMode
} from "utils"
import { removeForceOffline, setForceOffline } from "@redux"
import { useClickOutside } from "hooks"
import SsSwitch from "./SsSwitch"
import SsItem from "./SsItem"
import SsTemporaryOff from "./SsTemporaryOff"
import SsProfile from "./SsProfile"
import LogoutButton from "./LogoutButton"
import { Navbar } from "components"
import SSPrinterCloud from "./SSPrinterCloud"
import SsAutoCheckout from "./SsAutoCheckout"
import SsFullscreen from "./SsFullscreen"
import SsSiteUpdater from "./SsSiteUpdater"
import classes from "./index.module.scss"

const SettingsSidebar = props => {
  const {
    currentTab,
    setCurrentTab,
    openSettingSidebar,
    setOpenSettingSidebar
  } = props

  const [t, i18n] = useTranslation()

  const dispatch = useDispatch()

  const history = useHistory()

  const forceOffline =
    useSelector(state => state.internetStatus.forceOffline)

  const {
    serviceTypeName
  } = useSelector(state => state.systemSettings)

  useClickOutside(() => {
    setOpenSettingSidebar(false)
  }, ["setting-sidebar", "setting-hamburger-button"])

  const handleToggleForceOffline = (checked) => {
    dispatch(checked
      ? setForceOffline()
      : removeForceOffline()
    )
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  const handleCreateCds = () => {
    cdsBroadcastChannel.create()

    window.open("/cds-checkout")
  }

  // const handleAutoCheckout = () => {
  //   dispatch(toggleAutoCheckout())
  // }

  const handleGoToKiosk = () => {
    history.push("/kiosk-foods")
  }

  const handleGoTo = (goToTab) => {
    setCurrentTab(goToTab)
  }

  const handleNavigate = (navigateToPage) => {
    history.push(navigateToPage)

    setCurrentTab("")
  }

  const kioskMode = isInKioskMode()

  const showPrintersTab =
    haveAuth(["printerAccess"])

  const showCloudPrintersTab =
    haveAuth(["printerAccess"])

  const showAutoConfirmTab =
    !kioskMode &&
    haveAuth([
      "placeOrder",
      "viewUnconfirmedOrders"
    ],
      "needMultipleAccesses"
    )

  const showTableDecorationTab =
    !kioskMode &&
    haveAuth(["tableAccess"]) &&
    serviceTypeName === "restaurant"

  const showRepositoryManagementTab =
    !kioskMode &&
    haveAuth(["repositoryManagement"]) &&
    serviceTypeName === "shop"

  const showStockTransferRequestTab =
    !kioskMode &&
    haveAuth(
      [
        "createStockTransfer",
        "respondStockTransfer",
        "receiveStockTransfer",
        "resolveStockTransfer"
      ],
      "needOneOfAccesses"
    )

  const showSubscriptionPlansTab =
    !kioskMode &&
    hasFullAccess()

  const showInventoryTab =
    !kioskMode &&
    haveAuth(["inventory"])

  const showOfflineMode =
    !kioskMode

  const showTemporaryOffMode =
    !kioskMode &&
    haveAuth(["closeStore"])

  const showReturnToKioskTab = isInKioskMode()

  const isLtr = i18n.dir(i18n.language) === "ltr"

  return (
    <div
      id="setting-sidebar"
      className={clsx(
        classes["container"],
        isLtr && classes["container-ltr"],
        openSettingSidebar && classes["show"],
        openSettingSidebar && isLtr && classes["show-ltr"],
      )}
    >
      <Navbar
        variant="compact"
        title={t("تنظیمات")}
        hideLeftIconInMobileView
        onLeftIconPress={() =>
          kioskMode
            ? handleGoToKiosk()
            : history.push("/foods")
        }
        rightSideComponent={() =>
          <button
            className={classes["close"]}
            onClick={() => setOpenSettingSidebar(false)}
          >
            <MdClose
              color="white"
              size={25}
            />
          </button>
        }
      />

      <SsProfile />

      <div className={classes["scroll"]}>
        {
          showReturnToKioskTab &&
          <SsItem
            label={t("بازگشت_به_کیوسک")}
            onClick={handleGoToKiosk}
          />
        }

        <SsItem
          label={t("شیفت")}
          goToTab="shiftCashier"
          currentTab={currentTab}
          onClick={() => handleGoTo("shiftCashier")}
        />

        {
          showPrintersTab &&
          <SsItem
            label={t("پرینتر_ها")}
            goToTab="printers"
            onClick={() => handleGoTo("printers")}
            currentTab={currentTab}
          />
        }

        <SsItem
          label={t("کارتخوان_های_من")}
          goToTab="myPosTerminals"
          onClick={() => handleGoTo("myPosTerminals")}
          currentTab={currentTab}
        />

        {
          showTableDecorationTab &&
          <SsItem
            label={t("چیدمان_میز_ها")}
            onClick={() => handleNavigate("/table-decoration")}
          />
        }

        {
          showRepositoryManagementTab &&
          <SsItem
            label={t("خروج_از_انبار")}
            onClick={() => handleNavigate("/repository-management")}
          />
        }

        {
          showRepositoryManagementTab &&
          <SsItem
            label={t("گزارش_انبارداری")}
            onClick={() => handleNavigate("/repository-report")}
          />
        }
{/* 
        {
          showStockTransferRequestTab &&
          <SsItem
            label={t("درخواست_موجودی")}
            onClick={() => handleNavigate("/stock-transfer-requests")}
          />
        } */}

        {
          showInventoryTab &&
          <SsItem
            label={t("فهرست_موجودی")}
            onClick={() => handleNavigate("/inventory")}
          />
        }

        {
          showSubscriptionPlansTab &&
          <SsItem
            label={t("حساب_کاربری")}
            onClick={() => handleNavigate("/subscription-plans")}
          />
        }

        <SsItem
          label={t("نمایشگر_مشتری")}
          onClick={handleCreateCds}
        />

        {
          showAutoConfirmTab &&
          <SsAutoCheckout />
        }

        {
          showCloudPrintersTab &&
          <SSPrinterCloud />
        }

        {
          showTemporaryOffMode &&
          <SsTemporaryOff />
        }

        {
          showOfflineMode &&
          <SsSwitch
            text={t("حالت_آفلاین")}
            onClick={handleToggleForceOffline}
            value={forceOffline}
          />
        }

        <SsFullscreen />

        {
          process.env.NODE_ENV === "development" &&
          <SsItem
            label={t("قالب پرینت ها")}
            onClick={() => handleNavigate("/printer-viewer")}
          />
        }

        <SsItem
          label={t("رفرش_سایت")}
          onClick={handleRefresh}
        />

        <LogoutButton />

        <SsSiteUpdater />
      </div>
    </div>
  )
}

export default SettingsSidebar